import type { ShopperPromotionsTypes } from 'commerce-sdk-isomorphic'

const PROMOTIONS_CACHE_MS = 5 * 60 * 1000

export const usePromotions = () => {
  const locale = useNuxtApp().$i18n.locale.value
  const result = ref<ShopperPromotionsTypes.Promotion[]>()
  const error = ref()

  const getPromotionsByIds = async (ids: string[]) => {
    if (!ids.length) {
      result.value = []
      return
    }
    try {
      const shopper = useShopper()
      // Kept in caller order: SFCC returns promotions in the order requested, which is the display order.
      const joinedIds = ids.join(',')
      // Promotion definitions aren't shopper-specific, so every list on the page can share one response.
      const promotions = await shareRequest(
        `promotions|${shopper.config.siteId}|${locale}|${joinedIds}`,
        async () =>
          shopper.shopperPromotions.getPromotions({
            headers: await shopper.getAuthHeaders(),
            parameters: {
              ids: joinedIds,
              siteId: shopper.config.siteId,
              ...sfccLocaleParams(locale),
            },
          }),
        PROMOTIONS_CACHE_MS,
      )
      if (!promotions.data) return
      result.value = promotions.data
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  const getApproachingDiscounts = async () => {
    try {
      const shopper = useShopper()
      const customApiPathParameters = {
        apiName: 'promotion',
        apiVersion: 'v1',
        endpointPath: 'approaching-discounts',
      }

      // SAFETY: callCustomEndpoint resolves to the JSON body of the approaching-discounts custom API,
      // whose contract is ApproachingDiscountResponse.
      const approachingDiscounts = (await shopper.helpers.callCustomEndpoint({
        options: {
          method: 'GET',
          parameters: {
            siteId: shopper.config.siteId,
            ...sfccLocaleParams(locale),
          },
          headers: await shopper.getAuthHeaders(),
          customApiPathParameters,
        },
        clientConfig: {
          parameters: shopper.config,
          proxy: `${resolveRequestOrigin()}/proxy`,
        },
      })) as ApproachingDiscountResponse
      return approachingDiscounts.approachingDiscounts
    } catch (e) {
      error.value = e
    }
  }

  return {
    getPromotionsByIds,
    getApproachingDiscounts,
    result: computed(() => result.value),
    error: computed(() => error.value),
  }
}
